import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Tooltip
} from '@mui/material';
import {
  Build as MaintenanceIcon,
  CheckCircle as CompletedIcon,
  Schedule as DelayedIcon,
  Cancel as RejectedIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getAllMaintenanceRecordsUnlimited } from '../../services/maintenanceService';
import { MaintenanceRecord, MaintenanceStatus } from '../../utils/types';
import { formatCurrency } from '../../utils/numberUtils';
import { statusIcons, statusLabels } from '../../utils/constants';

interface MaintenanceNotificationsProps {
  onClose?: () => void;
  maxItems?: number;
  filterStatus?: 'pending' | 'completed' | 'all';
}

interface MaintenanceNotification {
  id: string;
  type: 'completed' | 'delayed' | 'rejected';
  title: string;
  message: string;
  record: MaintenanceRecord;
  priority: 'high' | 'medium' | 'low';
  color: 'success' | 'warning' | 'error';
  icon: React.ReactNode;
}

const MaintenanceNotifications: React.FC<MaintenanceNotificationsProps> = ({
  onClose,
  maxItems = 10,
  filterStatus = 'all'
}) => {
  const [notifications, setNotifications] = useState<MaintenanceNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadMaintenanceNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const allRecords = await getAllMaintenanceRecordsUnlimited();
      const maintenanceNotifications: MaintenanceNotification[] = [];
      const now = new Date();

      allRecords.forEach(record => {
        // فلترة حسب النوع المطلوب
        if (filterStatus === 'completed' && record.status === MaintenanceStatus.COMPLETED) {
          // الأجهزة المكتملة في انتظار العميل
          maintenanceNotifications.push({
            id: `completed-${record.id}`,
            type: 'completed',
            title: 'جهاز جاهز للتسليم',
            message: `${record.customerName} - ${record.deviceType}`,
            record,
            priority: 'medium',
            color: 'success',
            icon: statusIcons[MaintenanceStatus.COMPLETED]
          });
        } else if (filterStatus === 'pending' && (record.status === MaintenanceStatus.PENDING || record.status === MaintenanceStatus.IN_PROGRESS)) {
          // الطلبات قيد الانتظار وقيد العمل
          const isPending = record.status === MaintenanceStatus.PENDING;
          maintenanceNotifications.push({
            id: `${isPending ? 'pending' : 'in-progress'}-${record.id}`,
            type: 'delayed',
            title: isPending ? 'طلب قيد الانتظار' : 'طلب قيد العمل',
            message: `${record.customerName} - ${record.deviceType}`,
            record,
            priority: 'medium',
            color: isPending ? 'warning' : 'warning',
            icon: statusIcons[record.status]
          });
        } else if (filterStatus === 'all') {
          // عرض جميع الإشعارات (المنطق الأصلي)

          // الطلبات قيد الانتظار
          if (record.status === MaintenanceStatus.PENDING) {
            maintenanceNotifications.push({
              id: `pending-${record.id}`,
              type: 'delayed',
              title: 'طلب قيد الانتظار',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'medium',
              color: 'warning',
              icon: statusIcons[MaintenanceStatus.PENDING]
            });
          }

          // الطلبات قيد العمل
          if (record.status === MaintenanceStatus.IN_PROGRESS) {
            maintenanceNotifications.push({
              id: `in-progress-${record.id}`,
              type: 'delayed',
              title: 'طلب قيد العمل',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'medium',
              color: 'warning',
              icon: statusIcons[MaintenanceStatus.IN_PROGRESS]
            });
          }

          // الأجهزة المكتملة في انتظار العميل
          if (record.status === MaintenanceStatus.COMPLETED) {
            maintenanceNotifications.push({
              id: `completed-${record.id}`,
              type: 'completed',
              title: 'جهاز جاهز للتسليم',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'medium',
              color: 'success',
              icon: statusIcons[MaintenanceStatus.COMPLETED]
            });
          }

          // الأجهزة المسلمة
          if (record.status === MaintenanceStatus.DELIVERED) {
            maintenanceNotifications.push({
              id: `delivered-${record.id}`,
              type: 'completed',
              title: 'جهاز تم تسليمه',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'low',
              color: 'success',
              icon: statusIcons[MaintenanceStatus.DELIVERED]
            });
          }

          // الأجهزة قيد العمل
          if (record.status === MaintenanceStatus.IN_PROGRESS) {
            maintenanceNotifications.push({
              id: `in-progress-${record.id}`,
              type: 'delayed',
              title: 'جهاز قيد العمل',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'high',
              color: 'warning',
              icon: statusIcons[MaintenanceStatus.IN_PROGRESS]
            });
          }

          // الأجهزة المتأخرة في الصيانة
          if (
            record.status === MaintenanceStatus.PENDING ||
            record.status === MaintenanceStatus.IN_PROGRESS
          ) {
            let isDelayed = false;
            let delayMessage = '';

            if (record.estimatedDeliveryDate) {
              const estimatedDate = new Date(record.estimatedDeliveryDate);
              if (estimatedDate < now) {
                const daysOverdue = Math.floor((now.getTime() - estimatedDate.getTime()) / (1000 * 60 * 60 * 24));
                isDelayed = true;
                delayMessage = `متأخر ${daysOverdue} يوم`;
              }
            } else {
              const receivedDate = new Date(record.receivedAt);
              const daysSinceReceived = Math.floor((now.getTime() - receivedDate.getTime()) / (1000 * 60 * 60 * 24));
              if (daysSinceReceived > 7) {
                isDelayed = true;
                delayMessage = `${daysSinceReceived} يوم بدون تقدير`;
              }
            }

            if (isDelayed) {
              maintenanceNotifications.push({
                id: `delayed-${record.id}`,
                type: 'delayed',
                title: 'صيانة متأخرة',
                message: `${record.customerName} - ${delayMessage}`,
                record,
                priority: 'high',
                color: 'warning',
                icon: statusIcons[record.status]
              });
            }
          }

          // الأجهزة المرفوضة
          if (record.status === MaintenanceStatus.CANCELLED) {
            maintenanceNotifications.push({
              id: `rejected-${record.id}`,
              type: 'rejected',
              title: 'جهاز مرفوض',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'low',
              color: 'error',
              icon: statusIcons[MaintenanceStatus.CANCELLED]
            });
          }

          // الأجهزة المرتجعة
          if (record.status === MaintenanceStatus.REFUNDED) {
            maintenanceNotifications.push({
              id: `refunded-${record.id}`,
              type: 'rejected',
              title: 'جهاز مرتجع',
              message: `${record.customerName} - ${record.deviceType}`,
              record,
              priority: 'low',
              color: 'error',
              icon: statusIcons[MaintenanceStatus.REFUNDED]
            });
          }
        }
      });

      // ترتيب الإشعارات حسب الأولوية
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      maintenanceNotifications.sort((a, b) => 
        priorityOrder[b.priority] - priorityOrder[a.priority]
      );

      setNotifications(maintenanceNotifications.slice(0, maxItems));
    } catch (err) {
      console.error('Error loading maintenance notifications:', err);
      setError('فشل في تحميل إشعارات الصيانة');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaintenanceNotifications();
  }, [maxItems, filterStatus]);

  const handleViewRecord = (recordId: string, orderNumber?: number) => {
    if (orderNumber) {
      navigate(`/admin/maintenance/order-${orderNumber}`);
    } else {
      navigate(`/admin/maintenance/${recordId}`);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleViewAllMaintenance = () => {
    navigate('/admin/maintenance-records');
    if (onClose) {
      onClose();
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          جاري تحميل إشعارات الصيانة...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert 
          severity="error" 
          action={
            <IconButton size="small" onClick={loadMaintenanceNotifications}>
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 350, maxWidth: 450 }}>
      {/* رأس الإشعارات */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MaintenanceIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              {filterStatus === 'pending' ? 'طلبات قيد الانتظار' :
               filterStatus === 'completed' ? 'طلبات جاهزة للتسليم' :
               'إشعارات الصيانة'}
            </Typography>
          </Box>
          <Tooltip title="تحديث">
            <IconButton size="small" onClick={loadMaintenanceNotifications}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {notifications.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {notifications.length} إشعار جديد
          </Typography>
        )}
      </Box>

      {/* قائمة الإشعارات */}
      {notifications.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <MaintenanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            لا توجد إشعارات صيانة جديدة
          </Typography>
        </Box>
      ) : (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => handleViewRecord(notification.record.id, notification.record.orderNumber)}
              >
                <ListItemIcon>
                  <Tooltip title={statusLabels[notification.record.status]} placement="top">
                    <Box sx={{ 
                      color: `${notification.color}.main`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 32,
                      height: 32
                    }}>
                      {statusIcons[notification.record.status]}
                    </Box>
                  </Tooltip>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        {notification.title}
                      </Typography>
                      <Chip
                        label={notification.type === 'completed' ? 'جاهز' : 
                              notification.type === 'delayed' ? 'متأخر' : 'مرفوض'}
                        size="small"
                        color={notification.color}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {formatCurrency(notification.record.finalCost || notification.record.initialCost || 0)} -
                        رقم الطلب: {notification.record.orderNumber || notification.record.id.substring(0, 8)}
                      </Typography>
                    </Box>
                  }
                />
                
                <ListItemSecondaryAction>
                  <Tooltip title="عرض التفاصيل">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewRecord(notification.record.id, notification.record.orderNumber);
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
              
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* أزرار الإجراءات */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleViewAllMaintenance}
          startIcon={<MaintenanceIcon />}
        >
          عرض جميع طلبات الصيانة
        </Button>
      </Box>
    </Box>
  );
};

export default MaintenanceNotifications;
